import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Truck, Wrench, ArrowRight, Warehouse } from "@phosphor-icons/react";

const ATELIER_ID = "__atelier__";

export default function WorkshopPanel() {
  const [equipment, setEquipment] = useState([]);

  const load = async () => {
    try { const r = await api.get("/equipment"); setEquipment(r.data); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  useEffect(() => { load(); }, []);

  const vehicles = equipment.filter((e) => e.kind === "vehicle");
  const tools = equipment.filter((e) => e.kind !== "vehicle");

  const move = async (toolId, targetId) => {
    try {
      const payload = targetId === ATELIER_ID
        ? { location_kind: "atelier", location_vehicle_id: null }
        : { location_kind: "vehicle", location_vehicle_id: targetId };
      await api.patch(`/equipment/${toolId}/location`, payload);
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const toolsAt = (id) => tools.filter((t) => {
    if (id === ATELIER_ID) return !t.location_kind || t.location_kind === "atelier";
    return t.location_kind === "vehicle" && t.location_vehicle_id === id;
  });

  const columns = [{ id: ATELIER_ID, name: "Atelier", subtitle: "Stock central", icon: Warehouse, color: "#F59E0B" }]
    .concat(vehicles.map((v) => ({ id: v.id, name: v.name, subtitle: v.plate || v.model || "Véhicule", icon: Truck, color: "#005CFF" })));

  return (
    <div className="space-y-4" data-testid="workshop-panel">
      <div className="rounded-2xl bg-white/[0.03] border border-white/10 p-4">
        <div className="font-display font-semibold">Atelier & Véhicules</div>
        <div className="text-xs text-white/50 mt-0.5">Suivez où se trouve chaque outil — atelier central ou dans un véhicule d&apos;intervention.</div>
      </div>

      {vehicles.length === 0 && (
        <div className="rounded-2xl bg-amber-500/10 border border-amber-500/30 p-3 text-xs text-amber-200">
          Aucun véhicule enregistré — ajoutez-en dans l&apos;onglet <b>Outillage</b> pour pouvoir affecter des outils.
        </div>
      )}

      <div className="grid grid-cols-1 gap-3">
        {columns.map((col) => {
          const IconC = col.icon;
          const items = toolsAt(col.id);
          return (
            <div key={col.id} className="rounded-2xl bg-white/[0.03] border border-white/10 overflow-hidden" data-testid={`workshop-col-${col.id}`}>
              <div className="p-3 flex items-center gap-3 border-b border-white/10">
                <div className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0" style={{ background: col.color + "22", color: col.color }}>
                  <IconC size={22} weight="duotone" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-display font-semibold text-sm truncate">{col.name}</div>
                  <div className="text-[11px] text-white/40 truncate">{col.subtitle}</div>
                </div>
                <div className="text-xs text-white/40 tabular-nums shrink-0">{items.length} outil{items.length > 1 ? "s" : ""}</div>
              </div>
              <div className="p-2 space-y-1.5 min-h-[60px]">
                {items.length === 0 && <div className="text-[11px] text-white/30 text-center py-3">Vide</div>}
                {items.map((t) => (
                  <div key={t.id} className="flex items-center gap-2 p-2 rounded-lg bg-black/30 border border-white/5" data-testid={`workshop-tool-${t.id}`}>
                    <Wrench size={13} className="text-white/40 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="text-xs truncate">{t.name}</div>
                      <div className="text-[10px] text-white/40 truncate">{[t.brand, t.model, t.serial].filter(Boolean).join(" · ") || "—"}</div>
                    </div>
                    <select
                      value={col.id}
                      onChange={(e) => move(t.id, e.target.value)}
                      className="bg-white/5 border border-white/10 rounded-md text-[10px] px-1.5 py-1 outline-none max-w-[45%] truncate"
                      data-testid={`workshop-move-${t.id}`}
                    >
                      <option value={ATELIER_ID}>Atelier</option>
                      {vehicles.map((v) => (<option key={v.id} value={v.id}>{v.name}</option>))}
                    </select>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
